import { createClient as createSupabaseClient } from '@/lib/supabase/client';
import { getFunctionsErrorMessage } from '@/lib/edgeFunction';

const SITE_URL = (process.env.NEXT_PUBLIC_SITE_URL ?? 'https://theoperatorcircle.com').replace(/\/+$/, '');

async function logEmail(
  supabase: ReturnType<typeof createSupabaseClient>,
  payload: {
    recipient_email: string;
    recipient_name: string;
    subject: string;
    template_name: string;
    status: string;
    resend_id?: string | null;
    error_message?: string | null;
  }
) {
  // Use SECURITY DEFINER RPC — email_logs RLS is admin-only, and access
  // requests are submitted by anonymous users who cannot insert directly.
  const { error } = await supabase.rpc('log_email', {
    p_recipient_email: payload.recipient_email,
    p_recipient_name: payload.recipient_name,
    p_subject: payload.subject,
    p_template_name: payload.template_name,
    p_status: payload.status,
    p_resend_id: payload.resend_id ?? null,
    p_error_message: payload.error_message ?? null,
  });
  if (error) {
    console.warn('email_logs write failed:', error.message);
  }
}

export async function sendEmail(
  templateName: string,
  recipientEmail: string,
  recipientName: string,
  data: Record<string, any> = {}
): Promise<{ success: boolean; error?: string }> {
  try {
    const supabase = createSupabaseClient();
    const { data: result, error } = await supabase.functions.invoke('send-email', {
      body: {
        template_name: templateName,
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        data: { ...data, portal_url: SITE_URL, admin_url: `${SITE_URL}/admin-dashboard` },
      },
    });

    if (error) throw error;
    if (result?.error) throw new Error(result.error);

    await logEmail(supabase, {
      recipient_email: recipientEmail,
      recipient_name: recipientName,
      subject: result?.subject ?? templateName,
      template_name: templateName,
      status: 'sent',
      resend_id: result?.id,
    });

    return { success: true };
  } catch (err: any) {
    const message = await getFunctionsErrorMessage(err, err?.message || 'Failed to send email');
    try {
      const supabase = createSupabaseClient();
      await logEmail(supabase, {
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        subject: templateName,
        template_name: templateName,
        status: 'failed',
        error_message: message,
      });
    } catch {}
    return { success: false, error: message };
  }
}
